import { useMutation } from "@tanstack/react-query";
import { Image, X } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { filesGenerateUploadUrl } from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";

export const ImageDropzone = ({
  value,
  onValueChange,
}: {
  value: string | null;
  onValueChange: (value: string | null) => void;
}) => {
  const [tempUrl, setTempUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const generateUploadUrlMutation = useMutation({
    mutationFn: () => convexClient.mutation(filesGenerateUploadUrl, {}),
  });

  const uploadFile = useCallback(
    async (file: File) => {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }

      // Validate file size (4MB max)
      if (file.size > 4 * 1024 * 1024) {
        toast.error("Image must be less than 4MB");
        return;
      }

      setIsUploading(true);

      try {
        const previewUrl = URL.createObjectURL(file);
        setTempUrl(previewUrl);

        const uploadUrl = await generateUploadUrlMutation.mutateAsync();
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });

        if (!result.ok) {
          throw new Error("Upload failed");
        }

        const json: unknown = await result.json();
        if (
          !json ||
          typeof json !== "object" ||
          !("storageId" in json) ||
          typeof json.storageId !== "string"
        ) {
          throw new Error("Upload response missing storageId");
        }

        onValueChange(json.storageId);
        URL.revokeObjectURL(previewUrl);
        setTempUrl(null);
      } catch {
        toast.error("Error uploading image");
        setTempUrl(null);
      } finally {
        setIsUploading(false);
      }
    },
    [generateUploadUrlMutation, onValueChange]
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      await uploadFile(file);
    },
    [uploadFile]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (!file) {
        return;
      }

      await uploadFile(file);
    },
    [uploadFile]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const imageSrc = tempUrl ?? value;

  if (imageSrc) {
    return (
      <div className="relative h-44 w-full sm:max-w-56 sm:min-w-56">
        <img
          alt="Uploaded content"
          className="h-44 w-full rounded-lg object-cover"
          height={176}
          src={imageSrc}
          width={224}
        />
        {isUploading && (
          <div className="bg-background/50 absolute inset-0 flex items-center justify-center rounded-lg">
            <div className="border-border border-t-foreground h-6 w-6 animate-spin rounded-full border-2" />
          </div>
        )}
        <button
          className="bg-background absolute top-2 right-2 rounded-full p-1"
          onClick={() => onValueChange(null)}
          type="button"
        >
          <X className="text-foreground size-4" />
          <span className="sr-only">Remove uploaded image</span>
        </button>
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Dropzone uses drag events for file upload; the label inside provides accessible interaction
    <div
      className="border-border flex h-44 w-full flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed p-4 sm:max-w-56 sm:min-w-56"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Image className="text-foreground" />
      <p className="body-sm text-muted-foreground text-center">
        Drag image here or{" "}
        <label
          className="label text-foreground cursor-pointer"
          htmlFor="image-upload"
        >
          click to upload
        </label>
      </p>
      <input
        accept="image/*"
        className="hidden"
        id="image-upload"
        onChange={handleFileChange}
        type="file"
      />
    </div>
  );
};
